/**
 * Play-box Client Wrapper for Bet99 Pick'em
 *
 * This module wraps the @low6/play-box GameClient to provide a simplified API
 * for iframe communication with the parent window (host).
 *
 * Key Responsibilities:
 * - Initialize the GameClient on app startup
 * - Send IFRAME_INITIALISED message to signal app is ready
 * - Request user authentication info from the host
 * - Handle responses from the host
 *
 * Message Flow:
 * 1. App starts → emitIframeReady() → Host knows iframe is loaded
 * 2. App needs auth → requestUserAuthInfo() → Host sends auth data
 * 3. Host responds → GameClient listener receives ResponseUserAuthInfoMessage
 */

import {
  GameClient,
  MessageTopic,
  LogLevel,
  ResponseUserAuthInfoMessage,
  PlayBoxMessage,
} from '@low6/play-box';

/**
 * Login status response format (matching old bridge-api format)
 */
export interface LoginStatusResponse {
  LoginStatus: number; // 0 = not logged in, 1 = logged in (E2E), 2 = logged in (SSO)
  SessionId?: string; // String representation of userId
  userId?: number; // NEW: Numeric userId from play-box
  Username?: string;
  token?: string; // SSO token from URL params
}

/**
 * Singleton GameClient instance
 */
let gameClientInstance: GameClient | null = null;

/**
 * Initialize the GameClient singleton
 * Should be called once on app startup
 */
export function initializePlayBoxClient(): GameClient {
  if (!gameClientInstance) {
    gameClientInstance = GameClient.getInstance({
      logLevel: import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR,
    });

    // Start listening for messages from host
    gameClientInstance.listenForMessages();

    console.log('[PlayBox] ⚙️ GameClient initialized', {
      logLevel: import.meta.env.DEV ? 'DEBUG' : 'ERROR',
      timestamp: new Date().toISOString(),
      isInIframe: window.self !== window.top,
      location: window.location.href,
    });
  }

  return gameClientInstance;
}

/**
 * Get the GameClient instance
 * Throws error if not initialized
 */
export function getGameClient(): GameClient {
  if (!gameClientInstance) {
    throw new Error(
      '[play-box-client] GameClient not initialized. Call initializePlayBoxClient() first.'
    );
  }
  return gameClientInstance;
}

/**
 * Emit IFRAME_INITIALISED message to host
 * Signals that the iframe app has finished loading and is ready
 *
 * This replaces the old bridgeApi.ready() / BRIDGE_API_READY message
 */
export function emitIframeReady(): void {
  const client = getGameClient();
  client.emitMessageToHost(MessageTopic.IFRAME_INITIALISED, null);

  console.log('[PlayBox] Emitted IFRAME_INITIALISED to host', {
    topic: MessageTopic.IFRAME_INITIALISED,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Request user authentication info from the host with automatic retry logic
 *
 * This replaces the old bridgeApi.getLoginStatus() / GET_LOGIN_STATUS message
 *
 * The host should respond with a RESPONSE_USER_AUTH_INFO message containing:
 * - userId: string
 * - token: string (JWT token)
 *
 * RETRY STRATEGY:
 * - Sends REQUEST_USER_AUTH_INFO message to host
 * - If no response within retryInterval, resends the message
 * - Uses exponential backoff: 500ms, 1000ms, 2000ms, 4000ms...
 * - Total timeout after all retries (default: 15 seconds)
 * - Prevents race condition where host hasn't registered listeners yet
 *
 * @param onResponse - Callback invoked when host responds with auth info
 * @param onTimeout - Optional callback invoked if no response after all retries
 * @param maxRetries - Maximum number of retry attempts (default: 5)
 * @param totalTimeout - Total timeout in milliseconds (default: 15000ms)
 */
export function requestUserAuthInfo(
  onResponse: (authInfo: ResponseUserAuthInfoMessage['data']) => void,
  onTimeout?: () => void,
  maxRetries: number = 5,
  totalTimeout: number = 15000
): void {
  const client = getGameClient();
  const listenerIdentifier = `auth-request-${Date.now()}`;
  let responseReceived = false;
  let retryCount = 0;
  let retryTimeoutId: NodeJS.Timeout | null = null;

  // Calculate retry intervals with exponential backoff
  const getRetryInterval = (attempt: number): number => {
    return Math.min(500 * Math.pow(2, attempt), 4000); // Cap at 4 seconds
  };

  // Register listener for the response
  client.registerListener(
    MessageTopic.RESPONSE_USER_AUTH_INFO,
    listenerIdentifier,
    (message: PlayBoxMessage) => {
      if (responseReceived) return; // Prevent duplicate handling
      responseReceived = true;

      // Clean up all timers
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      clearTimeout(totalTimeoutId);

      const response = message as ResponseUserAuthInfoMessage;

      console.log('[PlayBox] ✅ Received RESPONSE_USER_AUTH_INFO', {
        raw: response,
        userId: response.data.userId,
        hasToken: !!response.data.token,
        tokenPrefix: response.data.token?.substring(0, 20),
        retryCount,
        timestamp: new Date().toISOString(),
      });

      // Clean up listener after response
      client.unregisterListener(MessageTopic.RESPONSE_USER_AUTH_INFO, listenerIdentifier);

      // Invoke callback with auth data
      onResponse(response.data);
    }
  );

  // Function to send the request
  const sendRequest = () => {
    if (responseReceived) return;

    client.emitMessageToHost(MessageTopic.REQUEST_USER_AUTH_INFO, null);

    console.log('[PlayBox] 📤 Sent REQUEST_USER_AUTH_INFO to host', {
      attempt: retryCount + 1,
      maxAttempts: maxRetries + 1,
      timestamp: new Date().toISOString(),
    });
  };

  // Function to schedule next retry
  const scheduleRetry = () => {
    if (responseReceived || retryCount >= maxRetries) return;

    const interval = getRetryInterval(retryCount);
    retryCount++;

    console.log('[PlayBox] ⏳ No response yet, scheduling retry', {
      nextAttempt: retryCount + 1,
      maxAttempts: maxRetries + 1,
      retryDelay: `${interval}ms`,
      timestamp: new Date().toISOString(),
    });

    retryTimeoutId = setTimeout(() => {
      sendRequest();
      scheduleRetry();
    }, interval);
  };

  // Set total timeout
  const totalTimeoutId = setTimeout(() => {
    if (responseReceived) return;

    responseReceived = true;

    // Clean up retry timer and listener
    if (retryTimeoutId) clearTimeout(retryTimeoutId);
    client.unregisterListener(MessageTopic.RESPONSE_USER_AUTH_INFO, listenerIdentifier);

    console.error('[PlayBox] ❌ Request for user auth info timed out', {
      totalAttempts: retryCount + 1,
      timeout: `${totalTimeout}ms`,
      timestamp: new Date().toISOString(),
    });

    if (onTimeout) {
      onTimeout();
    }
  }, totalTimeout);

  // Send initial request
  sendRequest();

  // Schedule first retry
  scheduleRetry();
}

/**
 * Helper function to convert play-box auth response to old LoginStatusResponse format
 * This maintains backward compatibility with existing AuthContext code
 *
 * @param authInfo - Response from host
 * @param tokenFromUrl - Token extracted from URL params
 * @returns LoginStatusResponse in the expected format
 */
export function convertToLoginStatusResponse(
  authInfo: ResponseUserAuthInfoMessage['data'],
  tokenFromUrl?: string
): LoginStatusResponse {
  // Determine login status
  let loginStatus = 0; // not logged in

  if (authInfo.token || tokenFromUrl) {
    loginStatus = 2; // SSO logged in
  } else if (authInfo.userId) {
    loginStatus = 1; // E2E test logged in
  }

  // Handle both numeric and string userId for backward compatibility
  // Test harness now sends numeric userId, but we support both formats
  const sessionId = typeof authInfo.userId === 'number' ? String(authInfo.userId) : authInfo.userId;

  const numericUserId: number | undefined =
    typeof authInfo.userId === 'number' ? authInfo.userId : undefined;

  return {
    LoginStatus: loginStatus,
    SessionId: sessionId,
    userId: numericUserId, // NEW: Include numeric userId if available
    Username: undefined, // Username not provided by play-box, will be fetched from backend
    token: authInfo.token || tokenFromUrl,
  };
}

/**
 * Request app exit (navigate user away from the iframe)
 * Useful for logout flows or redirecting to external pages
 */
export function requestAppExit(): void {
  const client = getGameClient();
  client.emitMessageToHost(MessageTopic.REQUEST_APP_EXIT, null);

  console.log('[PlayBox] Sent REQUEST_APP_EXIT to host', {
    topic: MessageTopic.REQUEST_APP_EXIT,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Register a generic listener for any message topic
 * Useful for debugging or handling custom messages
 *
 * @param topic - The message topic to listen for
 * @param listenerId - Unique identifier for this listener
 * @param callback - Callback invoked when message is received
 */
export function registerMessageListener(
  topic: MessageTopic,
  listenerId: string,
  callback: (message: PlayBoxMessage) => void
): void {
  const client = getGameClient();
  client.registerListener(topic, listenerId, callback);
}

/**
 * Unregister a previously registered listener
 *
 * @param topic - The message topic
 * @param listenerId - The listener identifier
 */
export function unregisterMessageListener(topic: MessageTopic, listenerId: string): void {
  const client = getGameClient();
  client.unregisterListener(topic, listenerId);
}

/**
 * Export for backward compatibility with old bridge-api interface
 * This allows minimal changes to existing code
 */
export const playBoxClient = {
  initialize: initializePlayBoxClient,
  ready: emitIframeReady,
  getLoginStatus: (onResponse: (response: LoginStatusResponse) => void, onTimeout?: () => void) => {
    // Extract token from URL if present (matching old behavior)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token') || undefined;

    requestUserAuthInfo(authInfo => {
      const response = convertToLoginStatusResponse(authInfo, tokenFromUrl);
      onResponse(response);
    }, onTimeout);
  },
  requestExit: requestAppExit,
  registerListener: registerMessageListener,
  unregisterListener: unregisterMessageListener,
};
